import {
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  type LocalDataColumn,
  SearchInput,
  StatusBadge,
  TOUCH,
  useDebouncedValue,
} from "@nymbl/nos2-rules";
import { KeyRound, Link2, MoreHorizontal, Plus, Shield, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { SettingsPage } from "@/components/layout/SettingsPage";
import { type User, useUsers } from "@/hooks/use-users";
import { useSession } from "@/lib/auth-client";
import { ChangeRoleDialog } from "./ChangeRoleDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { LinkPersonDialog } from "./LinkPersonDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "resetPassword"; user: User }
  | { type: "changeRole"; user: User }
  | { type: "linkPerson"; user: User }
  | { type: "delete"; user: User };

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
];
const ROLE_TONES = { admin: "info", user: "neutral" } as const;
const ROLE_LABELS = { admin: "Admin", user: "User" };

const COLUMNS: LocalDataColumn<User>[] = [
  {
    key: "name",
    label: "Name",
    type: "text",
    accessor: (user) => user.name,
    render: (user) => <span className="font-medium">{user.name}</span>,
  },
  {
    key: "email",
    label: "Email",
    type: "text",
    accessor: (user) => user.email,
    render: (user) => <span className="text-muted-foreground">{user.email}</span>,
  },
  {
    key: "role",
    label: "Role",
    type: "enum",
    options: ROLE_OPTIONS,
    accessor: (user) => user.role,
    render: (user) => <StatusBadge status={user.role} tones={ROLE_TONES} labels={ROLE_LABELS} />,
  },
  {
    key: "created",
    label: "Created",
    type: "date",
    accessor: (user) => new Date(user.createdAt).toISOString().slice(0, 10),
    render: (user) => (
      <span className="text-muted-foreground">{new Date(user.createdAt).toLocaleDateString()}</span>
    ),
  },
];

export function UsersTable() {
  const { data: session } = useSession();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  const {
    data: users = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useUsers({ search: debouncedSearch });

  function closeDialog() {
    setDialog({ type: "none" });
  }

  return (
    <SettingsPage
      actions={
        <Button className={TOUCH} onClick={() => setDialog({ type: "create" })}>
          <Plus className="size-4" aria-hidden />
          Create user
        </Button>
      }
    >
      <DataTable<User>
        rows={users}
        loading={isLoading}
        error={isError ? (error?.message ?? "Failed to load users") : undefined}
        onRetry={() => refetch()}
        rowKey={(user) => user.id}
        label="Users"
        columns={COLUMNS}
        empty={{ title: "No users found", icon: Users }}
        controls={
          // Reads the whole sign-in list on the server, past the 100 rows this table holds.
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by email…"
            aria-label="Search users by email"
            className="w-full sm:w-64"
          />
        }
        rowActions={(user) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${user.name} (${user.email})`}
                disabled={user.id === session?.user?.id}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setDialog({ type: "changeRole", user })}>
                <Shield className="h-4 w-4" />
                Change role
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDialog({ type: "linkPerson", user })}>
                <Link2 className="h-4 w-4" />
                Link Nymbl Person
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDialog({ type: "resetPassword", user })}>
                <KeyRound className="h-4 w-4" />
                Reset password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDialog({ type: "delete", user })}
              >
                <Trash2 className="h-4 w-4" />
                Delete user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      {dialog.type === "create" && (
        <CreateUserDialog open onOpenChange={(open) => !open && closeDialog()} />
      )}

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "changeRole" && (
        <ChangeRoleDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "linkPerson" && (
        <LinkPersonDialog
          key={dialog.user.id}
          open
          onOpenChange={(open) => !open && closeDialog()}
          user={dialog.user}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteUserDialog open onOpenChange={(open) => !open && closeDialog()} user={dialog.user} />
      )}
    </SettingsPage>
  );
}
